import { formatDistance } from "../../shared/filters";
import { placeTypeLabelMap } from "../../shared/placeTypes";
import type {
  AggregateBreakdownResponse,
  ConstraintLevel,
  PlaceSelection,
  SearchFilters,
} from "../../shared/types";

interface LocationSnapshotPanelProps {
  location: PlaceSelection | null;
  radiusMeters: number;
  filters: SearchFilters;
  summaryCount?: number;
  summaryLoading: boolean;
  summaryError?: string;
  breakdown: AggregateBreakdownResponse | null;
  breakdownLoading: boolean;
  breakdownError?: string;
}

function getAmenityLabel(type: string): string {
  return placeTypeLabelMap.get(type) ?? type;
}

function getHeadlineLabel(scoreMode?: AggregateBreakdownResponse["scoreMode"]): string {
  return scoreMode === "gap" ? "Coverage gap signal" : "Location fit";
}

function getConstraintCopy(level: ConstraintLevel, scoreMode: AggregateBreakdownResponse["scoreMode"]) {
  if (scoreMode === "gap") {
    if (level === "constrained") {
      return "Thin nearby coverage";
    }

    if (level === "balanced") {
      return "Mixed nearby coverage";
    }

    return "Coverage already present";
  }

  if (level === "constrained") {
    return "Current constraint";
  }

  if (level === "balanced") {
    return "Moderate support";
  }

  return "Strong support";
}

export function LocationSnapshotPanel({
  location,
  radiusMeters,
  filters,
  summaryCount,
  summaryLoading,
  summaryError,
  breakdown,
  breakdownLoading,
  breakdownError,
}: LocationSnapshotPanelProps) {
  if (!location && !summaryError) {
    return (
      <div className="results results--snapshot">
        <section className="results__empty">
          <p className="eyebrow">Location snapshot</p>
          <h2>Search for a property, neighborhood, or corridor.</h2>
          <p>
            This view is tuned for quick multifamily due diligence: choose a thesis pack, adjust
            the distance bands if needed, and compare how the site performs inside the search
            radius.
          </p>
        </section>
      </div>
    );
  }

  const counts = breakdown?.counts ?? [];

  return (
    <div className="results results--snapshot">
      <section className="results__hero">
        <div>
          <p className="eyebrow">Location snapshot</p>
          <h2>{location?.label ?? "Selected location"}</h2>
          <p className="results__subtle">
            {formatDistance(radiusMeters)} radius · {filters.includedTypes.length} amenity types
          </p>
          {breakdown ? (
            <div className="selected-type-strip">
              <span className="pill pill--muted">{breakdown.packLabel}</span>
              {breakdown.isPackCustomized ? (
                <span className="pill pill--muted">Customized</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="results__hero-stat">
          <span>{breakdown ? breakdown.overallScore.toFixed(1) : "—"}</span>
          <small>{getHeadlineLabel(breakdown?.scoreMode)}</small>
        </div>
      </section>

      <section className="results__section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Tailored score</p>
            <h3>{breakdown ? breakdown.packLabel : "Amenity fit"}</h3>
          </div>
          {breakdownLoading ? <span className="pill">Refreshing snapshot…</span> : null}
        </div>
        {summaryLoading ? <p className="status-line">Updating the radius total…</p> : null}
        {summaryError ? <p className="error-banner">{summaryError}</p> : null}
        {breakdownError ? <p className="error-banner">{breakdownError}</p> : null}
        {breakdown ? (
          <>
            <p className="results__subtle">
              {breakdown.packDescription} {breakdown.methodology}
            </p>
            <div className="selected-type-strip selected-type-strip--compact">
              {breakdown.resolvedBands.map((band) => (
                <span className="pill pill--muted" key={band.radiusMeters}>
                  {formatDistance(band.radiusMeters)} × {Math.round(band.weight * 100)}%
                </span>
              ))}
            </div>
            <div className="snapshot-highlights">
              {breakdown.sections.map((section) => (
                <article className="snapshot-highlight-card" key={section.id}>
                  <p className="eyebrow">{section.label}</p>
                  <h4>{section.score.toFixed(1)}/10</h4>
                  <p>{section.summary}</p>
                </article>
              ))}
            </div>
            <p className="results__subtle">
              {summaryCount?.toLocaleString() ?? "—"} selected-type places matched inside the
              radius.
            </p>
          </>
        ) : null}
      </section>

      <section className="results__section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Evidence</p>
            <h3>Nearby amenity signals</h3>
          </div>
        </div>
        <div className="snapshot-grid">
          {counts.map((item) => (
            <article className="snapshot-card" key={item.type}>
              <div className="snapshot-card__header">
                <h4>{getAmenityLabel(item.type)}</h4>
                <span>{item.score.toFixed(1)}/10</span>
              </div>
              <div className="snapshot-card__bar">
                <span style={{ width: `${Math.max(item.score * 10, item.score > 0 ? 8 : 0)}%` }} />
              </div>
              <p>
                {item.count.toLocaleString()} places in the selected radius. Target{" "}
                {item.target.toFixed(1)}.
              </p>
              <p className="snapshot-card__meta">
                {getConstraintCopy(item.constraintLevel, breakdown?.scoreMode ?? "fit")} ·{" "}
                {Math.round(item.attainment * 100)}% of target.
              </p>
              <div className="selected-type-strip selected-type-strip--compact">
                {item.bands.map((band) => (
                  <span className="pill pill--muted" key={`${item.type}-${band.radiusMeters}`}>
                    {formatDistance(band.radiusMeters)}: {band.cumulativeCount}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
